// AutoBidsHistoryChart.jsx
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// Maximum bids for the white background bars
const MAX_BIDS = 20;

const data = [
  { month: 'Jan', bids: 5, remaining: MAX_BIDS - 5 },
  { month: 'Feb', bids: 12, remaining: MAX_BIDS - 12 },
  { month: 'Mar', bids: 10, remaining: MAX_BIDS - 10 },
  { month: 'Apr', bids: 17, remaining: MAX_BIDS - 17 },
  { month: 'May', bids: 9, remaining: MAX_BIDS - 9 },
  { month: 'Jun', bids: 14, remaining: MAX_BIDS - 14 },
  { month: 'Jul', bids: 7, remaining: MAX_BIDS - 7 },
];

const AutoBidsTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const { month, bids } = payload[0].payload;
  const displayDate = month === 'Feb' ? 'Monday, Feb 17th' : month;

  return (
    <div className="rounded-lg bg-white border-2 border-primary-blue px-2 py-1 shadow-lg min-w-[150px]">
      <div className="text-lg font-semibold text-gray-900">{bids} Bids</div>
      <div className="text-sm text-gray-500">{displayDate}</div>
    </div>
  );
};

const AutoBidsHistoryChart = ({ 
  autoBidEnabled = false,
  onToggleAutoBid,
  autoBidType = 'all',
  onAutoBidTypeChange,
  updating = false
}) => {
  return (
    <div className="space-y-3 bg-background-body p-2 rounded-2xl border-[2px] border-light-blue">
      {/* Top Controls Section */}
      <div className="bg-light-blue-shade-20 rounded-2xl border-[2px] border-light-blue p-4 shadow-sm">
        <div className="flex flex-col gap-3">
          {/* Enable Auto Bidding toggle */}
          <div className="bg-white border-[1.5px]  border-primary-blue rounded-xl px-4 py-3.5 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Enable Auto Bidding
            </span>
            <button
              type="button"
              onClick={onToggleAutoBid}
              disabled={updating}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                autoBidEnabled ? 'bg-primary-blue' : 'bg-gray-300'
              } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  autoBidEnabled ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* Select AutoBid Type */}
          <div className="bg-white border-[1.5px]  border-primary-blue rounded-xl px-4 py-3 relative">
            <select
              id="auto-bid-type"
              value={autoBidType}
              onChange={(e) => onAutoBidTypeChange(e.target.value)}
              disabled={updating}
              className={`w-full bg-transparent text-sm text-gray-600 focus:outline-none cursor-pointer appearance-none pr-8 ${
                updating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <option value="all">All Types</option>
              <option value="fixed">Fixed</option>
              <option value="hourly">Hourly</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
              <svg className="h-5 w-5 text-gray-900" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {/* Optional loading indicator */}
          {updating && (
            <div className="text-xs text-gray-500 text-center">Saving...</div>
          )}
        </div>
      </div>

      {/* Chart Section */}
      <div className="bg-light-blue-shade-20 rounded-2xl border-[2px] border-light-blue shadow-sm">
        <h3 className="text-xl m-4 font-semibold text-gray-900 ">
          Auto Bids History
        </h3>

        <div className="bg-light-blue-shade-20 rounded-xl px-3 py-[9px]">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={data}
              barCategoryGap="15%"
              margin={{ top: 10, right: 10, bottom: 5, left: 10 }}
            >
              <XAxis
                dataKey="month"
                tick={{ fontSize: 13, fill: '#9CA3AF', fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
                dy={8}
              />
              <YAxis hide domain={[0, MAX_BIDS]} />
              <Tooltip content={<AutoBidsTooltip />} cursor={false} />

              <Bar
                dataKey="bids"
                stackId="stack"
                barSize={50}
                radius={[4, 4, 4, 4]}
                fill="#2563EB"
              />
              
              <Bar
                dataKey="remaining"
                stackId="stack"
                barSize={50}
                radius={[4, 4, 0, 0]}
                fill="#FFFFFF"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AutoBidsHistoryChart;
